import type { Party, PosSale, PosSaleStatus, PosTerminal } from '@erp/shared';
import { Receipt } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/shadcn/index.js';
import { Badge, Button, Card, DataTable, type DataTableColumn, EmptyState, Skeleton } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { appUrl } from '../../lib/basePath.js';
import { formatDateTime, formatNumber } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';
import { fetchAllParties } from '../../lib/parties.js';
import { listUsers, type UserListItem } from '../../lib/users.js';
import { userNote, useShowNotes } from '../documents/docNotes.js';
import { SaleProfitCard } from '../reports/_shared/SaleProfitCard.js';

import { PosSaleDetailBody, PosSaleDetailSkeleton, SALE_STATUS_TONE, usePosSaleDetail } from './PosSaleDetail.js';

/** One request's worth of sales — the endpoint's own maximum. */
const PAGE = 500;
/**
 * The most the page loads with NO date range: the newest this many. Past it the
 * page says so and points at the dates, which go to the server and so reach a
 * sale of any age without dragging a year of them into the browser.
 */
const MAX_ROWS = 5000;

/** The sale's day on THIS computer's calendar — a 00:30 sale in Duhok is that day, not the UTC day before. */
function localDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function PosSalesPage(): JSX.Element {
  const { t, i18n } = useTranslation('pos');
  const { t: tc } = useTranslation();
  const [items, setItems] = useState<PosSale[] | null>(null);
  const [parties, setParties] = useState<Party[]>([]);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [terminals, setTerminals] = useState<PosTerminal[]>([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // A Notes column for whoever wants it — remembered in this browser.
  const [showNotes, setShowNotes] = useShowNotes('pos-sales');
  const [statusFilter, setStatusFilter] = useState('all');
  // Branch filter — client-side over the fetched page, like status/date. Rows
  // carry the sale's header warehouse; old, pre-branches sales have none.
  const [warehouseFilter, setWarehouseFilter] = useState('all');
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedSaleId, setSelectedSaleId] = useState<string | null>(null);
  /** Sales the server has for these dates, and whether more pages are still coming. */
  const [loadState, setLoadState] = useState<{ have: number; total: number; loading: boolean }>({ have: 0, total: 0, loading: false });
  /** Bumped per load, so a slow page from earlier dates can't land on newer ones. */
  const loadSeq = useRef(0);

  /**
   * EVERY sale, not the first 200. This used to ask for `?limit=200` and stop,
   * and the search, the filters and the table only ever saw those 200. It now
   * pages through 500 at a time — up to the newest MAX_ROWS, or everything in the
   * chosen dates, which ride the request as the whole local day (from 00:00 to
   * 23:59:59 here) so the last day of a range is never cut off.
   */
  useEffect(() => {
    const seq = ++loadSeq.current;
    const cap = fromDate || toDate ? Number.POSITIVE_INFINITY : MAX_ROWS;
    const params: Record<string, string | number> = {};
    if (fromDate) params.from = new Date(`${fromDate}T00:00:00`).toISOString();
    if (toDate) params.to = new Date(`${toDate}T23:59:59.999`).toISOString();
    void (async () => {
      const rows: PosSale[] = [];
      setLoadState((s) => ({ ...s, have: 0, loading: true }));
      try {
        for (let offset = 0; ; offset += PAGE) {
          const r = await api().get<{ items: PosSale[]; total?: number }>('/pos/sales', {
            params: { ...params, limit: PAGE, offset },
          });
          if (seq !== loadSeq.current) return; // the dates changed under us
          rows.push(...r.data.items);
          const total = r.data.total ?? rows.length;
          const done = r.data.items.length < PAGE || rows.length >= total || rows.length >= cap;
          setItems([...rows]);
          setLoadState({ have: rows.length, total, loading: !done });
          if (done) return;
        }
      } catch {
        if (seq === loadSeq.current) setLoadState((s) => ({ ...s, loading: false }));
      }
    })();
  }, [fromDate, toDate]);

  // Directories for the detail dialog's customer/cashier/terminal names.
  // Loaded once and best-effort — a lookup miss just shows "—", never blocks the page.
  useEffect(() => {
    void (async () => {
      try {
        // The WHOLE book, not the first page — a receipt names one customer out
        // of thousands, and a one-page directory could not resolve most of them.
        setParties(await fetchAllParties<Party>());
      } catch { /* non-fatal */ }
    })();
    void (async () => {
      try {
        setUsers(await listUsers());
      } catch { /* non-fatal */ }
    })();
    void (async () => {
      try {
        const r = await api().get<{ items: PosTerminal[] }>('/pos/terminals');
        setTerminals(r.data.items);
      } catch { /* non-fatal */ }
    })();
    void (async () => {
      try {
        const r = await api().get<{ items: Array<{ id: string; name: string }> }>('/warehouses');
        setWarehouses(r.data.items);
      } catch { /* non-fatal */ }
    })();
  }, []);

  const partyById = useMemo(() => new Map(parties.map((p) => [p.id, p])), [parties]);
  const userById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  const terminalById = useMemo(() => new Map(terminals.map((tm) => [tm.id, tm])), [terminals]);

  const { detail: selectedSale, loading: selectedLoading } = usePosSaleDetail(selectedSaleId);

  if (items === null) return <Card><Skeleton className="h-32 w-full" /></Card>;

  const header = (
    <div>
      <h1 className="text-2xl font-semibold">{t('sales.title')}</h1>
      <p className="text-sm text-fg-muted">{t('sales.subtitle')}</p>
    </div>
  );

  // Truly nothing — not "nothing in the chosen dates", which keeps the filters on
  // screen so the range can be changed back.
  if (items.length === 0 && !fromDate && !toDate) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <EmptyState
          icon={<Receipt className="h-8 w-8" aria-hidden />}
          title={t('sales.empty')}
          description={t('sales.emptyDescription')}
        />
      </div>
    );
  }

  // Date-range + status filters — same pattern as DocumentListPage (the table
  // itself handles search/sort/paginate over whatever this leaves).
  const statuses = Array.from(new Set(items.map((r) => r.status)));
  const anyFilter = fromDate !== '' || toDate !== '' || statusFilter !== 'all' || warehouseFilter !== 'all';
  const filteredItems = items.filter((row) => {
    if (statusFilter !== 'all' && row.status !== statusFilter) return false;
    if (warehouseFilter !== 'all' && row.warehouseId !== warehouseFilter) return false;
    const d = localDay(row.saleDate);
    if (fromDate && d < fromDate) return false;
    if (toDate && d > toDate) return false;
    return true;
  });

  const columns: DataTableColumn<PosSale>[] = [
    {
      id: 'number',
      header: t('sales.table.number'),
      sortValue: (s) => s.saleNumber,
      searchValue: (s) => s.saleNumber,
      cellClassName: 'font-mono text-xs',
      cell: (s) => s.saleNumber,
    },
    {
      id: 'date',
      header: t('sales.table.date'),
      sortValue: (s) => s.saleDate,
      cellClassName: 'text-xs text-fg-muted',
      cell: (s) => formatDateTime(s.saleDate, { locale: i18n.language }),
    },
    {
      id: 'customer',
      header: t('sales.table.customer', { defaultValue: 'Customer' }),
      sortValue: (s) => partyById.get(s.partyId)?.displayName ?? '',
      searchValue: (s) => partyById.get(s.partyId)?.displayName ?? '',
      cellClassName: 'text-sm',
      cell: (s) => partyById.get(s.partyId)?.displayName ?? '—',
    },
    // The note typed on the sale (register cart / tender box), searchable.
    ...(showNotes
      ? [{
        id: 'notes',
        header: tc('documents:form.notes', { defaultValue: 'Notes' }),
        searchValue: (s: PosSale) => userNote(s.notes),
        cellClassName: 'text-xs max-w-[18rem]',
        cell: (s: PosSale) => {
          const note = userNote(s.notes);
          return note
            ? <span className="line-clamp-2 whitespace-pre-line" title={note}><bdi>{note}</bdi></span>
            : <span className="text-fg-muted">—</span>;
        },
      } satisfies DataTableColumn<PosSale>]
      : []),
    {
      id: 'terminal',
      header: t('sales.table.terminal'),
      sortValue: (s) => terminalById.get(s.terminalId)?.name ?? '',
      cellClassName: 'text-xs text-fg-muted',
      cell: (s) => terminalById.get(s.terminalId)?.name ?? '—',
    },
    {
      id: 'total',
      header: t('sales.table.total'),
      align: 'end',
      sortValue: (s) => Number(s.total),
      cell: (s) => formatMoney(s.total, { locale: i18n.language, currency: s.currencyCode }),
    },
    {
      id: 'status',
      header: t('sales.table.status'),
      sortValue: (s) => t(`sales.status.${s.status}`),
      searchValue: (s) => t(`sales.status.${s.status}`),
      cell: (s) => <Badge tone={SALE_STATUS_TONE[s.status as PosSaleStatus]}>{t(`sales.status.${s.status}`)}</Badge>,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {header}
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-fg-muted">{tc('filters.from', { defaultValue: 'From' })}</span>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-fg-muted">{tc('filters.to', { defaultValue: 'To' })}</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand" />
        </label>
        {warehouses.length > 1 && (
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-fg-muted">{tc('filters.branch', { defaultValue: 'Branch' })}</span>
            <select value={warehouseFilter} onChange={(e) => setWarehouseFilter(e.target.value)} className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand">
              <option value="all">{tc('filters.allBranches', { defaultValue: 'All branches' })}</option>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </label>
        )}
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-fg-muted">{t('sales.table.status')}</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand">
            <option value="all">{tc('filters.allStatuses', { defaultValue: 'All statuses' })}</option>
            {statuses.map((s) => (
              <option key={s} value={s}>{t(`sales.status.${s}`)}</option>
            ))}
          </select>
        </label>
        <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-border bg-bg px-2.5 text-sm text-fg">
          <input
            type="checkbox"
            checked={showNotes}
            onChange={(e) => setShowNotes(e.target.checked)}
            className="h-4 w-4 accent-[var(--color-brand)]"
          />
          {tc('documents:columnsShared.showNotes', { defaultValue: 'Show notes' })}
        </label>
        {anyFilter && (
          <Button variant="ghost" size="sm" onClick={() => { setFromDate(''); setToDate(''); setStatusFilter('all'); setWarehouseFilter('all'); }}>
            {tc('filters.clear', { defaultValue: 'Clear' })}
          </Button>
        )}
      </div>
      {/* How much of the list is here — still arriving, or capped with the way to the rest. */}
      {loadState.loading ? (
        <p className="text-xs text-fg-muted">
          {t('sales.loadingRows', {
            have: formatNumber(loadState.have, { locale: i18n.language }),
            total: formatNumber(loadState.total, { locale: i18n.language }),
            defaultValue: 'Loading {{have}} of {{total}}…',
          })}
        </p>
      ) : loadState.total > items.length ? (
        <p className="text-xs text-warning">
          {t('sales.rowsCapped', {
            have: formatNumber(items.length, { locale: i18n.language }),
            total: formatNumber(loadState.total, { locale: i18n.language }),
            defaultValue: 'Showing the newest {{have}} of {{total}} — pick a date range to see older ones.',
          })}
        </p>
      ) : null}
      <Card className="overflow-hidden p-0">
        <DataTable
          columns={columns}
          data={filteredItems}
          rowKey={(s) => s.id}
          searchPlaceholder={t('sales.table.number')}
          onRowClick={(s) => setSelectedSaleId(s.id)}
        />
      </Card>

      <Dialog open={!!selectedSaleId} onOpenChange={(open) => { if (!open) setSelectedSaleId(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('sale.viewDetails', { defaultValue: 'View details' })}</DialogTitle>
          </DialogHeader>
          {selectedLoading || !selectedSale ? (
            <PosSaleDetailSkeleton />
          ) : (
            <>
              <PosSaleDetailBody
                detail={selectedSale}
                partyName={partyById.get(selectedSale.sale.partyId)?.displayName}
                cashierName={userById.get(selectedSale.sale.cashierUserId)?.fullName}
                terminalName={terminalById.get(selectedSale.sale.terminalId)?.name}
                onOpenInvoice={
                  selectedSale.sale.invoiceId
                    ? () => window.open(appUrl(`/sales/invoices/${selectedSale.sale.invoiceId}/print`), '_blank')
                    : undefined
                }
              />
              {/* Here rather than inside PosSaleDetailBody: the register embeds
                  that same body for the cashier on shift, and margins are not
                  the till's business. The card gates itself on `profits.read`
                  and only mounts while this dialog is open, so a closed list
                  costs nothing. */}
              <SaleProfitCard
                sale={{ docType: 'pos_sale', docId: selectedSale.sale.id }}
                className="p-3"
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
